/**
 * Admin › Access — who is waiting to get in, and what letting them in gives them.
 * Pass 15 rev B, 3 September 2026.
 *
 * WHY THIS IS ITS OWN PAGE. The screen that decided who gets a team lived inside History ›
 * Managers, three levels down a section nobody opens between drafts, and there was nothing anywhere
 * to say somebody was waiting. The dot on the avatar, the count on the profile menu's Admin row and
 * this page are halves of the same fix: the app knows, so the app should say.
 *
 * ADMITTING SOMEBODY IS TWO WRITES, to two different places on purpose:
 *
 *   THE PAIRING is the server's. `POST /api/confirm` ties the Yahoo account to a manager id, and
 *   only a confirmed admin link may call it. A browser cannot write `links/{uid}` at all. The
 *   server also refuses a seat another account already sits in (Pass 15), so two admits racing from
 *   two tabs cannot both land.
 *   THE ROLE is the roster's. `managers/{id}` is admin-writable, and role is a property of the
 *   PERSON rather than of the Yahoo account. Tim's call, 21 August.
 *
 * GM AND VIEWER ARE WHAT CAN BE GRANTED HERE. Commissioner is not a property of an account — the
 * two commissioners are named in League configuration — and Admin is deliberately not offered: a
 * screen that can promote a stranger to the level that runs it is one mis-tap from locking its
 * owner out.
 *
 * Top to bottom: the door · waiting · rejected, kept · already in, grouped by rank.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  LOCKED_MESSAGE, ROLE_CHOICES, admissionProblems, claimedSeats, isWaiting, needsTeam, teamFor,
  rejectedLinks, type AccessLink, type GrantedRole, accountLabel, isAnonymous,
} from '../domain/access.js';
import { allManagers, currentManagers, managerShort } from '../domain/identity.js';
import { useAccessRequests } from './useAccessRequests.js';
import { dock } from './dock.js';
import { atLeast, type LeagueRoles, type LeagueStore, type Role } from '../store/types.js';

interface Props { gm: string; role: Role; store: LeagueStore }

/** A serialised server timestamp, or nothing. Firestore's admin SDK sends `{_seconds}`; JSON sends a string. */
function when(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'string') { const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; }
  if (typeof v === 'object') {
    const o = v as { _seconds?: number; seconds?: number };
    const secs = o._seconds ?? o.seconds;
    if (typeof secs === 'number') return new Date(secs * 1000);
  }
  return null;
}
const dayMonth = (d: Date) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

export function AccessRequests({ gm, role, store }: Props) {
  const { links, token, error, reload } = useAccessRequests(role);
  const [config, setConfig] = useState<LeagueRoles | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** The admitted account whose Edit dialog is open. */
  const [editing, setEditing] = useState<AccessLink | null>(null);

  useEffect(() => {
    let live = true;
    void store.leagueConfig().then((c) => { if (live) setConfig(c); });
    return () => { live = false; };
  }, [store]);

  /** Team name → manager id, from the roster. Every seat has a manager; this is who. */
  const seatsTaken = useMemo(() => {
    const out: Record<string, string> = {};
    for (const m of currentManagers()) if (m.team) out[m.team] = m.id;
    return out;
  }, []);
  /** Manager id → the uid of the account already sitting in that seat. */
  const claimedBy = useMemo(() => claimedSeats(links ?? []), [links]);
  const everyone = useMemo(() => allManagers().map((m) => m.id).sort(), []);

  const locked = config?.accessLocked === true;

  async function call(path: string, body: unknown, uidBusy: string) {
    setBusy(uidBusy); setErr(null);
    try {
      const res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json() as { error?: string }).error ?? `HTTP ${res.status}`);
      await reload();
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That did not save.');
      return false;
    } finally { setBusy(null); }
  }

  const setRejected = async (uid: string, rejected: boolean) => {
    if (await call('/api/reject', { uid, rejected }, uid)) {
      setSaid(rejected ? 'Turned away. It is in Rejected if that was a mistake.' : 'Back in the queue.');
    }
  };

  /**
   * Admit, or re-seat from the Edit dialog. Pairing first; if the roster write fails afterwards
   * the account is paired to a manager who already had a role, which is recoverable. The other
   * order would give somebody a role on a roster entry no account can reach.
   */
  const admit = async (a: { uid: string; gm: string; role: GrantedRole; team?: string | null }) => {
    const ok = await call('/api/confirm', {
      uid: a.uid, gm: a.gm, confirmed: true, viewer: a.role === 'viewer',
    }, a.uid);
    if (!ok) return;
    const person = allManagers().find((m) => m.id === a.gm);
    const team = teamFor(a);
    await store.saveManager({
      id: a.gm,
      name: person?.name ?? a.gm,
      /* Approving somebody is not seating him: a viewer keeps whatever status he had. */
      status: a.role === 'viewer' ? (person?.status ?? 'former') : 'current',
      role: a.role,
      ...(team ? { team } : {}),
      ...(person?.mark ? { mark: person.mark } : {}),
      ...(person?.order !== undefined ? { order: person.order } : {}),
      ...(person?.seasons?.length ? { seasons: person.seasons } : {}),
      addedBy: gm.toUpperCase(),
      addedAt: new Date().toISOString(),
    });
    setSaid(`${managerShort(a.gm)} is in as ${a.role === 'manager' ? 'a GM' : 'a viewer'}.`);
    await reload();
  };

  /** Free the seat, keep the account: it goes back to Waiting, to be paired again. */
  const unlink = async (uid: string) => {
    if (await call('/api/confirm', { uid, gm: null, confirmed: false, viewer: false }, uid)) {
      setSaid('Unlinked. The account is back in Waiting and the seat is free.');
    }
  };

  async function setLocked(next: boolean) {
    setBusy('lock');
    try {
      const c: LeagueRoles = {
        adminGms: config?.adminGms ?? [gm.toUpperCase()],
        commissionerGms: config?.commissionerGms ?? [],
        ...config,
        accessLocked: next,
        lockedBy: gm.toUpperCase(),
        lockedAt: new Date().toISOString(),
      };
      await store.saveLeagueConfig(c);
      setConfig(c);
      setSaid(next ? 'Requests are closed. Everyone already signed in still works.' : 'Requests are open again.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That did not save.');
    } finally { setBusy(null); }
  }

  if (!atLeast(role, 'admin')) return null;

  const queue = links?.filter(isWaiting) ?? [];
  const admitted = links?.filter((l) => l.confirmed) ?? [];
  const rejected = links ? rejectedLinks(links) : [];

  /** Rank of an admitted account, most permissions first. */
  const rankOf = (l: AccessLink): 0 | 1 | 2 | 3 => {
    if (l.viewer) return 3;
    const id = l.gm?.toUpperCase();
    if (id && (config?.adminGms ?? ['TIM']).includes(id)) return 0;
    if (id && (config?.commissionerGms ?? []).includes(id)) return 1;
    const person = allManagers().find((m) => m.id === id);
    if (person?.role === 'admin') return 0;
    if (person?.role === 'commissioner') return 1;
    return 2;
  };
  const RANK_LABEL = ['Admin', 'Commissioner', 'GM', 'Viewer'] as const;
  const groups = [0, 1, 2, 3].map((r) => ({
    rank: r, label: RANK_LABEL[r]!,
    rows: admitted.filter((l) => rankOf(l) === r)
      .sort((a, b) => (a.gm ?? '').localeCompare(b.gm ?? '')),
  })).filter((g) => g.rows.length > 0);

  /*
   * THE OFFLINE BUILD has no server to ask. One sentence, a legitimate permanent state, rather than
   * a page of empty cards pretending the queue is empty.
   */
  if (token === null) {
    return (
      <div className="app admin">
        <section className="card"><p className="fact pad">Only works signed in on the deployed site.</p></section>
      </div>
    );
  }

  return (
    <div className="app admin access">
      {/* ---------------------------------------------------------------- the door */}
      <section className="card door">
        <div className="doorhead">
          <div>
            <h3>{locked ? 'Requests are closed' : 'Accepting requests'}</h3>
            <p className="fact">
              {locked
                ? `New Yahoo accounts are turned away with a named message: “${LOCKED_MESSAGE}” Everyone already signed in keeps working.`
                : 'Closing turns new Yahoo accounts away with a named message. Everyone already signed in keeps working — including anyone who loses a session or buys a phone.'}
            </p>
          </div>
          <button type="button" className="btn" disabled={busy === 'lock'} onClick={() => void setLocked(!locked)}>
            {locked ? 'Reopen requests' : 'Lock requests'}
          </button>
        </div>
        <p className="cardfoot fact">
          {config?.lockedAt
            ? `Last changed ${dayMonth(new Date(config.lockedAt))} by ${managerShort(config.lockedBy ?? gm)}`
            : 'Never changed'}
          {said && <span className="said"> · {said}</span>}
        </p>
      </section>

      {(error || err) && <p className="estaxbad pad">{error ?? err}</p>}

      {/* ---------------------------------------------------------------- waiting */}
      <section className="card waiting">
        <div className="cardhead">
          <span className="peyebrow">Waiting</span>
          {queue.length > 0 && <span className="pill amber mono">{queue.length}</span>}
        </div>
        {token && queue.length === 0 && (
          <p className="fact pad">Nobody is waiting. New sign-ins appear here, and a dot appears on your avatar.</p>
        )}
        {token === undefined && <p className="fact pad">Reading the accounts…</p>}
        <ul className="accqueue">
          {queue.map((l) => (
            <AdmitRow key={l.uid} link={l} seatsTaken={seatsTaken} claimedBy={claimedBy}
              everyone={everyone} busy={busy === l.uid}
              onReject={() => void setRejected(l.uid, true)}
              onAdmit={(a) => admit(a)} />
          ))}
        </ul>
        <p className="cardfoot fact">
          GM and Viewer can be granted here. Commissioner is not a property of an account — the two
          commissioners are named in League configuration. Admin is deliberately not offered: a
          screen that can promote a stranger to the level that runs it is one mis-tap from locking
          its owner out.
        </p>
      </section>

      {/* ---------------------------------------------------------------- rejected, kept */}
      {rejected.length > 0 && (
        <section className="card rejected">
          <div className="cardhead"><span className="peyebrow">Rejected · kept</span></div>
          <ul className="accrejected">
            {rejected.map((l) => {
              const at = when(l.rejectedAt);
              return (
                <li key={l.uid}>
                  <s className="accname">{accountLabel(l)}</s>
                  <span className="mut">
                    rejected{at ? ` ${dayMonth(at)}` : ''}{l.rejectedBy ? ` by ${managerShort(l.rejectedBy)}` : ''}
                  </span>
                  <button type="button" className="linkbtn" disabled={busy === l.uid}
                    onClick={() => void setRejected(l.uid, false)}>
                    Undo
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ---------------------------------------------------------------- already in */}
      <section className="card alreadyin">
        <div className="cardhead"><span className="peyebrow">Already in</span></div>
        {admitted.length === 0 && <p className="fact pad">Nobody admitted yet.</p>}
        {groups.map((g) => (
          <div className="rankgroup" key={g.rank}>
            <span className="peyebrow ranklabel">{g.label}</span>
            <ul className="accin">
              {g.rows.map((l) => {
                const person = l.gm ? allManagers().find((m) => m.id === l.gm) : undefined;
                return (
                  <li key={l.uid}>
                    <b className="accname">{l.gm ? managerShort(l.gm) : 'unpaired'}</b>
                    <span className="accemail">{l.email ?? ''}</span>
                    <span className="accteam">{l.viewer ? '' : (person?.team ?? '')}</span>
                    <button type="button" className="linkbtn" onClick={() => setEditing(l)}>Edit</button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </section>

      {editing && (
        <EditAccount link={editing} me={gm} seatsTaken={seatsTaken} claimedBy={claimedBy}
          everyone={everyone} busy={busy === editing.uid}
          onClose={() => setEditing(null)}
          onSave={async (a) => { await admit(a); setEditing(null); }}
          onUnlink={async () => { await unlink(editing.uid); setEditing(null); }}
          onRemove={async () => { await setRejected(editing.uid, true); setEditing(null); }} />
      )}
    </div>
  );
}

/**
 * The seat select, shared by the waiting row and the edit dialog.
 *
 * UNCLAIMED SEATS FIRST, claimed ones below with their current owner named. Picking a claimed one
 * still runs into the conflict sentence — the ordering is a hint, the sentence is the guard.
 */
function SeatSelect(
  { value, onChange, seatsTaken, claimedBy, selfUid, disabled }: {
    value: string; onChange: (team: string) => void;
    seatsTaken: Readonly<Record<string, string>>;
    claimedBy: Readonly<Record<string, string>>;
    selfUid: string;
    disabled?: boolean;
  },
) {
  const teams = Object.keys(seatsTaken).sort();
  const free = teams.filter((t) => !claimedBy[seatsTaken[t]!.toUpperCase()] || claimedBy[seatsTaken[t]!.toUpperCase()] === selfUid);
  const taken = teams.filter((t) => !free.includes(t));
  return (
    <select value={value} disabled={disabled} aria-label="Seat" onChange={(e) => onChange(e.target.value)}>
      <option value="">Seat</option>
      {free.map((t) => <option key={t} value={t}>{t}</option>)}
      {taken.length > 0 && (
        <optgroup label="Already has an account">
          {taken.map((t) => (
            <option key={t} value={t}>{t} — {managerShort(seatsTaken[t]!)}</option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

function AdmitRow(
  { link, seatsTaken, claimedBy, everyone, busy, onAdmit, onReject }: {
    link: AccessLink;
    seatsTaken: Readonly<Record<string, string>>;
    claimedBy: Readonly<Record<string, string>>;
    everyone: string[];
    busy: boolean;
    onAdmit: (a: { uid: string; gm: string; role: GrantedRole; team?: string | null }) => Promise<void>;
    onReject: () => void;
  },
) {
  const [role, setRole] = useState<GrantedRole>('manager');
  const [team, setTeam] = useState<string>(() => {
    const guess = link.gm ? Object.entries(seatsTaken).find(([, holder]) => holder === link.gm) : null;
    return guess?.[0] ?? '';
  });
  const [who, setWho] = useState(link.gm ?? '');
  const [arming, setArming] = useState(false);

  /* For a GM the seat IS the identity; for a viewer he is a person on the roster with no seat. */
  const gmId = role === 'manager' ? (team ? seatsTaken[team] ?? '' : '') : who;
  const admission = { uid: link.uid, gm: gmId, role, team: role === 'manager' ? team : null };
  const problems = admissionProblems(admission, { seatsTaken, claimedBy });
  const conflict = problems.find((p) => /already .*seat/.test(p));
  const ready = problems.length === 0;

  return (
    <li className="accrow">
      <div className="acchead">
        <span className="accface" aria-hidden="true">
          {link.photoURL ? <img src={link.photoURL} alt="" /> : '?'}
        </span>
        <div className="accwho">
          <b className="accname">{accountLabel(link)}</b>
          <span className="mut">
            {isAnonymous(link) ? 'Anonymous Yahoo account — no name shared'
              : `Yahoo${link.email?.trim() && link.displayName?.trim() ? ` · ${link.email}` : ''}`}
            {link.teamName && ` · Yahoo team: ${link.teamName}`}
          </span>
        </div>
        {isAnonymous(link) && <span className="pill grey mono">anonymous</span>}
        {/* Reject needs nothing decided: you do not have to know who a stranger is to keep him out. */}
        <button type="button" className="linkbtn danger" disabled={busy} onClick={onReject}>Reject</button>
      </div>
      <div className="accfields">
        <select value={role} aria-label="Role" onChange={(e) => { setRole(e.target.value as GrantedRole); setArming(false); }}>
          {ROLE_CHOICES.map((c) => <option key={c.role} value={c.role}>{c.label}</option>)}
        </select>
        {needsTeam(role) ? (
          <SeatSelect value={team} onChange={(t) => { setTeam(t); setArming(false); }}
            seatsTaken={seatsTaken} claimedBy={claimedBy} selfUid={link.uid} />
        ) : (
          <select value={who} aria-label="Who is this" onChange={(e) => setWho(e.target.value)}>
            <option value="">Who is this?</option>
            {everyone.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
          </select>
        )}
        {arming ? (
          <span className="armed">
            <span className="fact">Admit as {gmId ? `${managerShort(gmId)}’s ${role === 'manager' ? 'GM' : 'viewer'}` : 'this'}?</span>
            <button type="button" className="btn primary" disabled={busy || !ready}
              onClick={() => void onAdmit(admission)}>
              {busy ? 'Admitting…' : 'Admit'}
            </button>
            <button type="button" className="linkbtn" onClick={() => setArming(false)}>Not yet</button>
          </span>
        ) : (
          <button type="button" className="btn primary" disabled={busy || !ready} onClick={() => setArming(true)}>
            Admit
          </button>
        )}
      </div>
      {conflict && <p className="conflict">{conflict}</p>}
    </li>
  );
}

/**
 * Role and seat for an account already in. Unlink frees the seat and keeps the account (it goes
 * back to Waiting); Remove arms first and lands in Rejected · kept. Nothing here is deleted.
 */
function EditAccount(
  { link, me, seatsTaken, claimedBy, everyone, busy, onClose, onSave, onUnlink, onRemove }: {
    link: AccessLink;
    me: string;
    seatsTaken: Readonly<Record<string, string>>;
    claimedBy: Readonly<Record<string, string>>;
    everyone: string[];
    busy: boolean;
    onClose: () => void;
    onSave: (a: { uid: string; gm: string; role: GrantedRole; team?: string | null }) => Promise<void>;
    onUnlink: () => Promise<void>;
    onRemove: () => Promise<void>;
  },
) {
  const [role, setRole] = useState<GrantedRole>(link.viewer ? 'viewer' : 'manager');
  const [team, setTeam] = useState<string>(() => {
    const seat = link.gm ? Object.entries(seatsTaken).find(([, g]) => g === link.gm) : null;
    return seat?.[0] ?? '';
  });
  const [who, setWho] = useState(link.gm ?? '');
  const [removing, setRemoving] = useState(false);
  const gmId = role === 'manager' ? (team ? seatsTaken[team] ?? '' : '') : who;
  const admission = { uid: link.uid, gm: gmId, role, team: role === 'manager' ? team : null };
  const problems = admissionProblems(admission, { seatsTaken, claimedBy });
  const conflict = problems.find((p) => /already .*seat/.test(p));
  const self = link.gm?.toUpperCase() === me.toUpperCase();
  const confirmed = when(link.confirmedAt);
  const meaning = ROLE_CHOICES.find((c) => c.role === role)?.what ?? '';

  return dock(
    <>
      <button type="button" className="sheetscrim kdscrim" aria-label="Close" onClick={onClose} />
      <div className="kdsheet editaccount" role="dialog" aria-modal="true" aria-label={`Edit ${link.gm ? managerShort(link.gm) : 'account'}`}>
        <h2 className="kdsheeth">Edit — {link.gm ? managerShort(link.gm) : accountLabel(link)}</h2>
        <p className="kdsheetsub">
          {link.email ?? accountLabel(link)}
          {confirmed && ` · confirmed ${dayMonth(confirmed)}`}
        </p>
        <label className="editfield">
          <span className="peyebrow">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as GrantedRole)}>
            {ROLE_CHOICES.map((c) => <option key={c.role} value={c.role}>{c.label}</option>)}
          </select>
          <span className="fact">{meaning}</span>
        </label>
        {needsTeam(role) ? (
          <label className="editfield">
            <span className="peyebrow">Team · 2026 seat</span>
            <span className="seatrow">
              <SeatSelect value={team} onChange={setTeam} seatsTaken={seatsTaken} claimedBy={claimedBy} selfUid={link.uid} />
              <button type="button" className="linkbtn danger" disabled={busy || self} onClick={() => void onUnlink()}>Unlink</button>
            </span>
            <span className="fact">
              Unlinking keeps the account and frees the seat; it goes back to Waiting. A GM needs a seat before the draft.
            </span>
          </label>
        ) : (
          <label className="editfield">
            <span className="peyebrow">Who is this</span>
            <select value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="">—</option>
              {everyone.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
            </select>
          </label>
        )}
        {conflict && <p className="conflict">{conflict}</p>}
        <div className="kdsheetacts editacts">
          {/* Not on your own row: an admin who turns himself away loses the screen that could undo it. */}
          {!self && (removing ? (
            <span className="armed">
              <span className="fact">Remove {link.gm ? managerShort(link.gm) : 'this account'} from the league?</span>
              <button type="button" className="linkbtn danger" disabled={busy} onClick={() => void onRemove()}>Remove</button>
              <button type="button" className="linkbtn" onClick={() => setRemoving(false)}>Keep</button>
            </span>
          ) : (
            <button type="button" className="linkbtn danger" disabled={busy} onClick={() => setRemoving(true)}>
              Remove from league
            </button>
          ))}
          <span className="spacer" />
          <button type="button" className="kdkeep" onClick={onClose}>Cancel</button>
          <button type="button" className="kdsubmit" disabled={busy || problems.length > 0}
            onClick={() => void onSave(admission)}>
            Save
          </button>
        </div>
      </div>
    </>,
  );
}
